/**
 * ProcessPerformanceWidget — „Wydajność procesów": paski terminowości per proces + dwa
 * wskaźniki kołowe (w terminie / zakończone) wg Figmy „08.6".
 * Kolor paska zależy od progu: ≥ 85 % zielony, ≥ 70 % niebieski (brand), ≥ 50 % bursztynowy,
 * poniżej czerwony. Wskaźnika „Odrzucone" z Figmy nie ma: w modelu dróg sprawa nie ma stanu
 * „odrzucona", a etap końcowy nie mówi, czy to zgoda, czy odmowa (decyzja właściciela 2026-10-10).
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useChartTheme } from '@/lib/use-chart-theme';

import { classifyDataError, fetchProcessPerformance } from '../dashboard-api';
import type { WidgetRenderProps } from '../types';
import { ProgressBar } from '../widget-ui';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

const GREEN = '#16a34a';
const AMBER = '#f59e0b';
const RED = '#dc2626';

/** Kolor wg progu dla wskaźnika „im wyżej, tym lepiej". */
export function thresholdColor(pct: number, brand: string): string {
  if (pct >= 85) return GREEN;
  if (pct >= 70) return brand;
  if (pct >= 50) return AMBER;
  return RED;
}

interface GaugeProps {
  pct: number;
  color: string;
  label: string;
}

/** Wskaźnik kołowy 0–100 % (SVG): tor + łuk + wartość w środku, podpis pod spodem. */
function Gauge({ pct, color, label }: GaugeProps) {
  const r = 16;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-11 w-11">
        <svg viewBox="0 0 40 40" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle
            cx="20"
            cy="20"
            r={r}
            fill="none"
            stroke="rgb(var(--border-secondary))"
            strokeWidth="4"
          />
          <circle
            cx="20"
            cy="20"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${(clamped / 100) * c} ${c}`}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums text-text-primary">
          {Math.round(clamped)}%
        </span>
      </div>
      <span className="text-[11px] text-text-tertiary">{label}</span>
    </div>
  );
}

export function ProcessPerformanceWidget({ widget, period = 'year' }: WidgetRenderProps) {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const { isAll, mode, companyId } = useActiveCompanyScope();
  const brand = theme.palette[0]!;
  const blocked = mode === 'none';

  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'process-performance', period, companyId ?? 'all'],
    queryFn: () => fetchProcessPerformance(period, isAll ? 'all' : 'company'),
    enabled: widget.type === 'process_performance' && !blocked,
    staleTime: 60_000,
    retry: false,
  });

  if (widget.type !== 'process_performance') return null;

  if (blocked) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const data = query.data;
  if (!data || data.processes.length === 0) {
    return <WidgetEmpty message={t('tenant.dashboard.widgets.process_performance.empty')} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ul className="space-y-2.5">
        {data.processes.map((p) => (
          <li key={p.processId} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0 truncate text-text-secondary" title={p.name}>
                {p.name}
              </span>
              <span className="shrink-0 tabular-nums font-medium text-text-primary">
                {Math.round(p.onTimePct)} %
              </span>
            </div>
            <ProgressBar pct={p.onTimePct} color={thresholdColor(p.onTimePct, brand)} />
          </li>
        ))}
      </ul>
      <div className="mt-auto flex items-start justify-around pt-1">
        <Gauge
          pct={data.gauges.onTimePct}
          color={thresholdColor(data.gauges.onTimePct, brand)}
          label={t('tenant.dashboard.widgets.process_performance.onTime')}
        />
        <Gauge
          pct={data.gauges.completedPct}
          color={thresholdColor(data.gauges.completedPct, brand)}
          label={t('tenant.dashboard.widgets.process_performance.completed')}
        />
      </div>
    </div>
  );
}
